import { twMerge } from 'tailwind-merge';
import { useState } from 'react';

import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
import { GetSpeciesTopWaters, GetSpeciesTopWatersVariables } from '../../../../interfaces/graphql';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { useIsMobileScreenWidthOrThinner } from '../../../../helpers/hooks/useScreenWidth';
import { FishingWaterCard } from '../../../../components/FishingWatersGrid/components/FishingWaterCard';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { useRutilusQuery } from '../../../../api/graphql/useRutilusQuery';
import { getSpeciesTopWaters } from '../../../../queries/rutilus/Species';
import { useGeolocation } from '../../../../contexts/GeolocationContext/GeolocationContext';
import { useClientHasRendered } from '../../../../helpers/hooks';
import { bbox } from '@turf/bbox';
import { buffer } from '@turf/buffer';
import { point } from '@turf/helpers';
import { turfBoundsToRutilusBounds } from '../../../../helpers/location';
import { getNodesFromConnection } from '../../../../helpers/graphql';
import { SUB_LINK_IDS } from '../../helpers/speciesHelpers';
import { Container } from '../../../../components/Container/Container';
import { InfoIcon } from '../../../../components/Icons/InfoIcon';
import { HeadlineLoader } from './components/HeadlineLoader';
import { NearbyWatersCTA } from './components/NearbyWatersCTA';
import { LocationInfoModal } from '../../../../components/LocationInfoModal/LocationInfoModal';
import { CardsLoader } from './components/CardsLoader';
import { NearbyWatersBanner } from './components/NearbyWatersBanner';

interface IProps {
  className?: string;
  speciesId: string;
  speciesName: string;
  title: string;
  sourceId?: string;
}

const RADIUS_OF_FISHINGSPOTS_IN_KM = 50; // Radius in kilometers for nearby fishing spots
const MAX_WATERS_TO_SHOW = 6; // Maximum number of waters to show

export const NearbyWatersSection = ({
  className,
  speciesId,
  speciesName,
  title = 'Best fishing spots',
  sourceId,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const { isLoading: currentLocationLoading, location } = useGeolocation();

  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [infoModalOpen, setIsInfoModalOpen] = useState(false);

  const latitude = location?.coordinates.latitude;
  const longitude = location?.coordinates.longitude;
  const country = location?.countryCode;
  const region = location?.regionName;

  const buffered =
    latitude && longitude
      ? buffer(point([longitude, latitude]), RADIUS_OF_FISHINGSPOTS_IN_KM, {
          units: 'kilometers',
        })
      : undefined;
  const turfbbox = buffered && bbox(buffered);
  const boundingBox = turfbbox && turfBoundsToRutilusBounds(turfbbox);

  const { data, loading } = useRutilusQuery<GetSpeciesTopWaters, GetSpeciesTopWatersVariables>(
    getSpeciesTopWaters,
    {
      variables: { speciesIDs: [speciesId], boundingBox, first: MAX_WATERS_TO_SHOW },
      skip: currentLocationLoading,
    },
  );

  const filteredWaters = getNodesFromConnection(data?.entry)
    .filter(water => water.catchesCount > 0)
    .sort((a, b) => b.catchesCount - a.catchesCount);

  const filteredWatersCount = filteredWaters.length;
  const isLoading = !clientHasRendered || loading || currentLocationLoading;

  const handleShowMoreClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyWatersSection',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const handleInfoButtonClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyWatersSection',
        source_id: sourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
    setIsInfoModalOpen(true);
  };

  return (
    <Container
      id={SUB_LINK_IDS.FISHING_SPOTS}
      className={twMerge(
        'w-full scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:scroll-mt-(--navigation-scrolltop-offset)',
        className,
      )}
    >
      {isLoading ? (
        <HeadlineLoader className="mb-4 sm:mb-8 md:mb-10" />
      ) : (
        <div className="mb-4 sm:mb-8 md:mb-10">
          <Heading className="mb-2" variant="sm" as="h2">
            {title}
          </Heading>
          {region && country && (
            <div className="flex items-center gap-1">
              <Text variant="sm" className="mb-0">
                Result for {region}, {country}
              </Text>
              <Button
                variant="textNone"
                className="body-text-sm flex"
                onClick={handleInfoButtonClick}
              >
                <InfoIcon width={16} height={16} />
              </Button>
            </div>
          )}
        </div>
      )}
      {!isLoading && filteredWaters.length === 0 && <NearbyWatersBanner />}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
        {isLoading ? (
          <CardsLoader />
        ) : (
          filteredWaters.map((fishingWater, index) => (
            <FishingWaterCard
              className={`test-${index} col-span-full sm:col-span-6 md:col-span-4`}
              fishingWater={fishingWater}
              key={fishingWater._id}
            />
          ))
        )}
      </div>
      {isMobileScreenWidthOrThinner && isAppDownloadModalOpen && (
        <NearbyWatersCTA speciesName={speciesName} />
      )}
      {filteredWatersCount >= MAX_WATERS_TO_SHOW && !isAppDownloadModalOpen && (
        <Button
          size="sm"
          className="mt-4 w-full p-2"
          variant="textPrimary"
          onClick={handleShowMoreClick}
        >
          Show more fishing spots
        </Button>
      )}
      <AppDownloadModal
        appDownloadSource="NearbyWatersSection"
        appDownloadSourceId={sourceId}
        modalHeader={'Find prime fishing spots anywhere'}
        modalHeaderVariant="sm"
        modalText="Track catches, find big fish hot spots and check the weekly quality rating of waters."
        isModalOpen={!isMobileScreenWidthOrThinner && isAppDownloadModalOpen}
        image={appdownloadPhone}
        imageMobile={appdownloadPhoneMobile}
        imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
        textWrapperClassName="mt-6"
        showQRCodeHint
        showTextOnMobile
      />
      <LocationInfoModal
        isModalOpen={infoModalOpen}
        onCloseModal={() => {
          setIsInfoModalOpen(false);
        }}
        radius={RADIUS_OF_FISHINGSPOTS_IN_KM}
      />
    </Container>
  );
};
